<template>
  <div class="servico-photo-upload">
    <div class="servico-header">
      <h5>#{{ servicoId }} - {{ servicoNome }}</h5>
      <Tag :value="`${photoCount} ${t('fotos')}`" :severity="photoCount > 0 ? 'success' : 'danger'" />
    </div>

    <!-- Photo thumbnails -->
    <div v-if="photos.length > 0" class="photo-thumbnails">
      <div v-for="photo in photos" :key="photo.imagem_id" class="photo-thumbnail">
        <img
          :src="photo.imagem_thumbnail_url"
          :alt="t('Foto do serviço')"
          @click="openLightbox(photo.imagem_url ?? '')"
        />
        <Button
          data-testid="servico-photo-excluir-btn"
          icon="pi pi-trash"
          severity="danger"
          size="small"
          rounded
          class="photo-delete-btn"
          @click="handleDeletePhoto(photo.imagem_id ?? 0)"
          :disabled="isDeleting"
        />
      </div>
    </div>

    <!-- Upload button -->
    <div class="upload-section">
      <!-- Dois inputs ocultos, sempre no DOM. Galeria SEM capture: com
           capture="environment" o Android pula o seletor e abre a câmera
           direto — foi assim que o check-in/check-out do compromisso ficou sem
           galeria (55a24fa3 forçou capture "para o tablet"). A câmera é um
           input SEPARADO, esse sim com capture; no modo `camera` o toque abre
           o popup Câmera/Galeria (mesmo padrão de SOReportAttributesList), no
           modo `any` vai direto ao seletor do sistema. -->
      <input
        ref="galleryInput"
        type="file"
        accept="image/*"
        @change="handleFileSelect"
        :id="`file-input-${servicoId}-${antesDepois}`"
        :data-testid="`servico-photo-input-${servicoId}-${antesDepois}`"
        class="file-input-visually-hidden"
        :disabled="isUploading"
      />
      <input
        ref="cameraInput"
        type="file"
        accept="image/*"
        capture="environment"
        @change="handleFileSelect"
        :id="`file-input-camera-${servicoId}-${antesDepois}`"
        :data-testid="`servico-photo-input-camera-${servicoId}-${antesDepois}`"
        class="file-input-visually-hidden"
        :disabled="isUploading"
      />
      <div
        role="button"
        tabindex="0"
        :data-testid="`servico-photo-upload-${servicoId}-${antesDepois}`"
        class="upload-button-label"
        :class="{ 'is-disabled': isUploading }"
        :aria-disabled="isUploading"
        @click="handleLabelClick"
        @keydown.enter.space.prevent="handleLabelClick"
      >
        <i :class="isUploading ? 'pi pi-spin pi-spinner' : 'pi pi-upload'"></i>
        {{ photos.length === 0 ? t('Enviar foto') : t('Adicionar mais') }}
      </div>
      <span v-if="isUploading" class="upload-progress">{{ t('Comprimindo e enviando...') }}</span>

      <!-- Popup: escolha entre câmera e galeria (só no modo camera) -->
      <template v-if="isPickerOpen">
        <div class="photo-picker-backdrop" @click="isPickerOpen = false" />
        <div class="photo-picker-popup" :data-testid="`servico-photo-picker-${servicoId}-${antesDepois}`">
          <div
            class="photo-picker-btn"
            role="button"
            tabindex="0"
            :data-testid="`servico-photo-picker-camera-${servicoId}-${antesDepois}`"
            @click="selectPhotoSource('camera')"
            @keydown.enter.space.prevent="selectPhotoSource('camera')"
          >
            <i class="pi pi-camera" />
            <span>{{ t('Câmera') }}</span>
          </div>
          <div
            class="photo-picker-btn"
            role="button"
            tabindex="0"
            :data-testid="`servico-photo-picker-gallery-${servicoId}-${antesDepois}`"
            @click="selectPhotoSource('gallery')"
            @keydown.enter.space.prevent="selectPhotoSource('gallery')"
          >
            <i class="pi pi-images" />
            <span>{{ t('Galeria') }}</span>
          </div>
        </div>
      </template>
    </div>

    <!-- Lightbox Modal -->
    <div v-if="showLightbox" class="photo-lightbox" @click="closeLightbox">
      <div class="lightbox-content">
        <button type="button" data-testid="servico-photo-lightbox-fechar-btn" class="lightbox-close" @click="closeLightbox">
          <i class="pi pi-times"></i>
        </button>
        <img :src="lightboxImage" :alt="t('Foto ampliada')" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useWizardServicos } from '../../../composables/useWizardServicos';
import { useConfirmAction } from '../../../composables/useConfirmAction';
import { eventBus } from '../../../eventBus';
import { log } from '../../../utils/DebugLogger';
import { compressImage } from '../../../utils/compressImage';
import type { ImagensServicos_FotoRow } from '../../../contracts/ImagensServicos_FotoRow';

type Id = number | string;

// A forma da foto é o CONTRATO gerado, não uma cópia local: a duplicata
// divergia do backend (id/nome são obrigatórios lá) e quebrava a atribuição.
type Photo = ImagensServicos_FotoRow;

interface Props {
  servicoId: number;
  servicoNome: string;
  antesDepois: 'a' | 'd';
  sourceMode?: 'camera' | 'any';
}

const props = withDefaults(defineProps<Props>(), {
  sourceMode: 'any'
});

const { t } = useI18n();
const { loadPhotos, uploadPhoto, deletePhoto } = useWizardServicos();
const { confirmAction } = useConfirmAction();

// State
const photos = ref<Photo[]>([]);
const isUploading = ref<boolean>(false);
const isDeleting = ref<boolean>(false);
const showLightbox = ref<boolean>(false);
const lightboxImage = ref<string>('');
const galleryInput = ref<HTMLInputElement | null>(null);
const cameraInput = ref<HTMLInputElement | null>(null);
const isPickerOpen = ref<boolean>(false);

// Computed
const photoCount = computed((): number => photos.value.length);

// Methods
const loadServicePhotos = async (): Promise<void> => {
  try {
    const beforeCount = photos.value.length;
    log('ServicoPhotoUpload', 'loadServicePhotos', 'Loading photos', {
      servicoId: props.servicoId,
      antesDepois: props.antesDepois,
      currentCount: beforeCount
    });
    
    const loadedPhotos = await loadPhotos(props.servicoId, props.antesDepois);
    
    log('ServicoPhotoUpload', 'loadServicePhotos', 'Received photos from API', {
      count: loadedPhotos.length,
      photos: loadedPhotos
    });
    
    // Force reactivity by creating a new array
    photos.value = [...loadedPhotos];
    
    const afterCount = photos.value.length;
    log('ServicoPhotoUpload', 'loadServicePhotos', 'Photos array updated', {
      servicoId: props.servicoId,
      beforeCount,
      afterCount,
      photoCount: photoCount.value,
      photos: photos.value.map(p => ({ id: p.imagem_id, name: p.imagem_nome }))
    });
  } catch (error) {
    log('ServicoPhotoUpload', 'loadServicePhotos', 'Error loading photos', {
      servicoId: props.servicoId,
      error
    });
    console.error(`❌ Error loading photos for service ${props.servicoId}:`, error);
    eventBus.emit('globalMessage.error', t('Erro ao carregar fotos'));
  }
};

const handleLabelClick = (): void => {
  if (isUploading.value) return;
  log('ServicoPhotoUpload', 'handleLabelClick', 'Upload trigger clicked', {
    isMobile: window.innerWidth <= 768,
    sourceMode: props.sourceMode,
    servicoId: props.servicoId,
    antesDepois: props.antesDepois,
  });
  if (props.sourceMode === 'camera') {
    // Tablet/celular: escolha explícita — câmera a um toque, galeria disponível.
    isPickerOpen.value = true;
    return;
  }
  // Modo any: seletor do sistema (input sem capture) direto.
  galleryInput.value?.click();
};

const selectPhotoSource = (kind: 'camera' | 'gallery'): void => {
  // aciona o input oculto correspondente (câmera usa capture; galeria não)
  (kind === 'camera' ? cameraInput.value : galleryInput.value)?.click();
  isPickerOpen.value = false;
};

const handleFileSelect = async (event: Event): Promise<void> => {
  const file = (event.target as HTMLInputElement).files?.[0];
  log('ServicoPhotoUpload', 'handleFileSelect', 'File selected', {
    hasFile: !!file,
    fileName: file?.name,
    isMobile: window.innerWidth <= 768
  });
  if (!file) return;

  // Validate file type
  if (!file.type.startsWith('image/')) {
    eventBus.emit('globalMessage.error', t('Por favor, selecione um arquivo de imagem'));
    return;
  }

  // Validate file size (max 50MB before compression)
  const maxSize = 50 * 1024 * 1024; // 50MB
  if (file.size > maxSize) {
    eventBus.emit('globalMessage.error', t('Arquivo muito grande. Tamanho máximo: 50MB'));
    return;
  }

  try {
    isUploading.value = true;
    const compressed = await compressImage(file);
    log('ServicoPhotoUpload', 'handleFileSelect', 'Image compressed', {
      originalSize: file.size,
      compressedSize: compressed.size,
      ratio: `${Math.round((1 - compressed.size / file.size) * 100)}%`
    });
    await handleUploadPhoto(compressed);
  } catch (compressionError) {
    log('ServicoPhotoUpload', 'handleFileSelect', 'Compression failed, uploading original', {
      error: compressionError
    });
    await handleUploadPhoto(file);
  } finally {
    isUploading.value = false;
  }

  // Clear file input
  for (const input of [galleryInput.value, cameraInput.value]) {
    if (input) input.value = '';
  }
};

const handleUploadPhoto = async (file: File): Promise<void> => {
  try {
    log('ServicoPhotoUpload', 'handleUploadPhoto', 'Uploading photo', {
      servicoId: props.servicoId,
      antesDepois: props.antesDepois,
      fileName: file.name
    });

    await uploadPhoto(props.servicoId, file, props.antesDepois);
    
    log('ServicoPhotoUpload', 'handleUploadPhoto', 'Photo uploaded to server');

    eventBus.emit('globalMessage.success', t('Foto enviada com sucesso'));

    // Reload photos in this component
    log('ServicoPhotoUpload', 'handleUploadPhoto', 'Reloading photos');
    await loadServicePhotos();
    
    log('ServicoPhotoUpload', 'handleUploadPhoto', 'Photos reloaded', {
      photoCount: photos.value.length
    });

    // Emit event to notify parent components to refresh
    log('ServicoPhotoUpload', 'handleUploadPhoto', 'Emitting photo-uploaded event');
    eventBus.emit('wizard-servicos.photo-uploaded', {
      servicoId: props.servicoId,
      antesDepois: props.antesDepois
    });
  } catch (error) {
    log('ServicoPhotoUpload', 'handleUploadPhoto', 'Error uploading photo', {
      error
    });
    console.error('❌ Error uploading photo:', error);
    eventBus.emit('globalMessage.error', t('Erro ao enviar foto'));
  }
};

const handleDeletePhoto = (imageId: number): void => {
  confirmAction({
    message: t('Tem certeza que deseja remover esta foto?'),
    header: t('Confirmação'),
    onAccept: async () => {
      try {
        log('ServicoPhotoUpload', 'handleDeletePhoto', 'Deleting photo', imageId);
        isDeleting.value = true;
        await deletePhoto(imageId);

        eventBus.emit('globalMessage.success', t('Foto removida com sucesso'));

        // Reload photos
        await loadServicePhotos();

        // Emit event to notify parent components
        eventBus.emit('wizard-servicos.photo-deleted', {
          servicoId: props.servicoId,
          antesDepois: props.antesDepois
        });

        log('ServicoPhotoUpload', 'handleDeletePhoto', 'Photo deleted successfully');
      } catch (error) {
        log('ServicoPhotoUpload', 'handleDeletePhoto', 'Error deleting photo', error);
        eventBus.emit('globalMessage.error', t('Erro ao remover foto'));
      } finally {
        isDeleting.value = false;
      }
    }
  });
};

const openLightbox = (imageName: string): void => {
  lightboxImage.value = imageName;
  showLightbox.value = true;
};

const closeLightbox = (): void => {
  showLightbox.value = false;
  lightboxImage.value = '';
};

// Listen to wizard refresh events
const handleWizardRefresh = (data?: { servicoId?: number }): void => {
  // Only refresh if this is for our service or a global refresh
  if (!data || data.servicoId === props.servicoId || !data.servicoId) {
    log('ServicoPhotoUpload', 'handleWizardRefresh', 'Refresh event received, reloading photos', {
      servicoId: props.servicoId,
      eventData: data
    });
    loadServicePhotos();
  }
};

// Lifecycle
onMounted(() => {
  log('ServicoPhotoUpload', 'mounted', 'Component mounted', {
    servicoId: props.servicoId,
    antesDepois: props.antesDepois
  });
  
  // Load initial photos
  loadServicePhotos();
  
  // Listen for refresh events from wizard
  eventBus.on('wizard-servicos.refresh-photos', handleWizardRefresh);
});

onBeforeUnmount(() => {
  log('ServicoPhotoUpload', 'beforeUnmount', 'Component unmounting', {
    servicoId: props.servicoId,
    antesDepois: props.antesDepois
  });
  
  // Clean up event listener
  eventBus.off('wizard-servicos.refresh-photos', handleWizardRefresh);
});
</script>

<style scoped>
.servico-photo-upload {
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 15px;
  margin-bottom: 15px;
  background-color: #fff;
}

.servico-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.servico-header h5 {
  margin: 0;
  font-size: 14px;
  font-weight: bold;
}

.badge {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  color: white;
}

.bg-success {
  background-color: #00a65a;
}

.bg-danger {
  background-color: #dd4b39;
}

.photo-thumbnails {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 10px;
}

.photo-thumbnail {
  position: relative;
  width: 80px;
  height: 80px;
  border: 1px solid #ddd;
  border-radius: 4px;
  overflow: hidden;
}

.photo-thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  cursor: pointer;
  transition: transform 0.2s;
}

.photo-thumbnail img:hover {
  transform: scale(1.05);
}

.photo-delete-btn {
  position: absolute;
  top: 2px;
  right: 2px;
  padding: 2px 6px;
  font-size: 10px;
  opacity: 0.8;
}

.photo-delete-btn:hover {
  opacity: 1;
}

.upload-section {
  position: relative;
}

/* Popup Câmera/Galeria — mesmo visual do laudo (SOReportAttributesList). */
.photo-picker-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1000;
}

.photo-picker-popup {
  position: absolute;
  bottom: calc(100% + 6px);
  left: 0;
  z-index: 1001;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 190px;
  padding: 6px;
  background: var(--p-surface-0, #fff);
  border: 1px solid var(--p-surface-200);
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
}

.photo-picker-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 6px;
  color: var(--p-text-color);
  font-size: 0.92rem;
  cursor: pointer;
  transition: background 0.15s ease;
}

.photo-picker-btn:hover {
  background: var(--p-surface-100);
}

.photo-picker-btn i {
  font-size: 1.05rem;
  color: var(--p-primary-color);
}

.file-input-visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.upload-button-label {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.5;
  border-radius: 0.375rem;
  cursor: pointer;
  user-select: none;
  transition: all 0.2s;
  border: 1px solid transparent;
  background-color: #6b7280;
  color: white;
  text-align: center;
  text-decoration: none;
}

.upload-button-label:hover {
  background-color: #4b5563;
}

.upload-button-label:active {
  transform: scale(0.98);
}

.upload-button-label.is-disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

.upload-button-label i {
  font-size: 1rem;
}

.upload-section {
  display: flex;
  align-items: center;
  gap: 10px;
  position: relative;
  z-index: 1;
}

.upload-progress {
  color: #666;
  font-size: 12px;
  font-style: italic;
}

/* Mobile specific styles */
@media (max-width: 768px) {
  .servico-photo-upload {
    padding: 12px;
    margin-bottom: 0;
  }

  .upload-section {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }

  .upload-button-label {
    width: 100%;
    justify-content: center;
    padding: 0.75rem 1rem;
    font-size: 1rem;
    touch-action: manipulation;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0.1);
  }
}

/* Lightbox styles */
.photo-lightbox {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.9);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.lightbox-content {
  position: relative;
  max-width: 90%;
  max-height: 90%;
}

.lightbox-content img {
  max-width: 100%;
  max-height: 90vh;
  object-fit: contain;
}

.lightbox-close {
  position: absolute;
  top: -40px;
  right: 0;
  background: transparent;
  border: none;
  color: white;
  font-size: 30px;
  cursor: pointer;
  padding: 10px;
}

.lightbox-close:hover {
  color: #ccc;
}
</style>
